import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { useState } from "react";
import { ScreenSizeSelectGuid } from "../screenSize/ScreenSizeSelectGuid";
import { TagSelectGuid } from "../tag/TagSelectGuid";
import { ContentTypeSelect } from "./components/ContentTypeSelect";
import { ContentFolderSelectSheet } from "./components/folder/ContentFolderSelectSheet";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Filter } from "lucide-react";

const formSchema = z.object({
  showExpired: z.boolean(),
  screenSizeId: z.string(),
  tagId: z.string(),
  contentType: z.string(),
  folderGuid: z.string().optional(),
});

export type FilterFormSchemaType = z.infer<typeof formSchema>;

interface Props {
  onChange: (values: FilterFormSchemaType) => void;
  defaultValues?: FilterFormSchemaType;
}

export const ContentCrudFilterForm: React.FC<Props> = ({ onChange, defaultValues }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<FilterFormSchemaType>(
    defaultValues || {
      showExpired: false,
      screenSizeId: "",
      tagId: "",
      contentType: "",
      folderGuid: "",
    }
  );

  const form = useForm<FilterFormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      showExpired: false,
    },
  });

  // Count active filters based on applied filters, not current form values
  const activeFiltersCount = Object.entries(appliedFilters).filter(([key, value]) => {
    if (key === "showExpired") return value === true;
    return value && value !== "";
  }).length;

  const handleApply = () => {
    const currentValues = form.getValues();
    setAppliedFilters(currentValues);
    onChange(currentValues);
    setOpen(false);
  };

  const handleClear = () => {
    form.reset({
      showExpired: false,
      screenSizeId: "",
      tagId: "",
      contentType: "",
      folderGuid: "",
    });
  };

  return (
    <div className="flex items-center justify-between">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            {t("Filters")}
            {activeFiltersCount > 0 && (
              <Badge variant="green" className="ml-1 h-5 px-2 text-xs">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-4" align="start">
          <Form {...form}>
            <div className="grid grid-cols-1 gap-4">
              <FormField
                control={form.control}
                name="showExpired"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <FormLabel>{t("Show expired")}</FormLabel>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="folderGuid"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Folder")}</FormLabel>
                    <FormControl>
                      <ContentFolderSelectSheet onSelectFolder={field.onChange} selectedFolderGuid={field.value} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="screenSizeId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Screen Size")}</FormLabel>
                    <FormControl>
                      <ScreenSizeSelectGuid field={field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tagId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Tag")}</FormLabel>
                    <FormControl>
                      <TagSelectGuid field={field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="contentType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Content Type")}</FormLabel>
                    <FormControl>
                      <ContentTypeSelect field={field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              <Button variant="outline" size="sm" onClick={handleClear}>
                {t("Clear")}
              </Button>
              <Button size="sm" onClick={handleApply}>
                {t("Apply")}
              </Button>
            </div>
          </Form>
        </PopoverContent>
      </Popover>
    </div>
  );
};
